import { createPortal } from "react-dom";
import { X, Share, Plus } from "lucide-react";

interface Props {
  onClose: () => void;
}

/**
 * Instruções de instalação para iOS/Safari, onde não existe prompt nativo:
 * o usuário adiciona à tela inicial manualmente pelo menu Compartilhar.
 *
 * Renderizado via portal no <body> para escapar de qualquer `transform`
 * ancestral (que quebraria o `fixed`/backdrop-blur — ver nota "modal-blur").
 */
export function PwaIOSInstructions({ onClose }: Props) {
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-neutral-950/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm bg-white dark:bg-neutral-100 rounded-t-3xl sm:rounded-3xl shadow-2xl border border-neutral-200 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
              <Share size={18} className="text-primary-600" />
            </div>
            <h3 className="font-display font-bold text-neutral-900 text-base tracking-tight">
              Instalar o Taskletto
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 -mr-1.5 -mt-1 rounded-lg hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800 cursor-pointer transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-neutral-600 mb-5">
          No iPhone ou iPad, adicione o app à tela inicial em dois passos:
        </p>

        <ol className="space-y-3">
          <li className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-neutral-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
              1
            </span>
            <span className="text-sm text-neutral-700 flex items-center gap-1.5 flex-wrap">
              Toque em
              <span className="inline-flex items-center gap-1 font-semibold text-neutral-900">
                <Share size={15} className="text-primary-600" /> Compartilhar
              </span>
              na barra do Safari.
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-neutral-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="text-sm text-neutral-700 flex items-center gap-1.5 flex-wrap">
              Escolha
              <span className="inline-flex items-center gap-1 font-semibold text-neutral-900">
                <Plus size={15} className="text-primary-600" /> Adicionar à Tela de Início
              </span>
              .
            </span>
          </li>
        </ol>

        <button
          onClick={onClose}
          className="mt-6 w-full py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white rounded-full text-sm font-semibold transition-colors cursor-pointer"
        >
          Entendi
        </button>
      </div>
    </div>,
    document.body,
  );
}
